/**
 * @file index.tsx
 *
 * @fileoverview Renders all the items from an order to be shown inside a cart component (JobCart or Cart).
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Price from '~/jobiqo/models/price';
import { CommerceOrder, CommerceOrderItem } from '~/jobiqo/types/types';
import { OrderItemQuantitySelector } from '~/jobiqo/components/Cart/components/OrderItemQuantitySelector';
import CartOrderInfoSlot from '~/jobiqo/components/Slots/CartOrderInfoSlot';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Hr } from '~/vcl/atoms/06-layout/10-hr';

interface CartOrderInfoProps {
  /**
   * The commerce order.
   */
  order: CommerceOrder;
  /**
   * Show quantity selector for items in the cart.
   *
   * @default "false"
   */
  showQuantities?: boolean;
  /**
   * When a user changes a product, this callback is fired with the updated item.
   */
  onChange?: (item: CommerceOrderItem) => void;
}

export const CartOrderInfo = ({
  order,
  showQuantities = false,
  onChange,
}: CartOrderInfoProps) => {
  const OrderPrice = new Price(order?.totalNumber, order?.totalCurrency);
  const OrderSubPrice = new Price(
    order?.subtotalNumber,
    order?.subtotalCurrency,
  );

  return (
    <>
      {order.items.map((orderItem, index) => {
        const OrderItemPrice = new Price(
          orderItem?.priceNumber,
          orderItem?.priceCurrency,
        );

        return (
          orderItem && (
            <div key={index}>
              <FlexItems justifyContent={'space-between'}>
                <FlexItem marginRight={6}>
                  <FlexItems
                    gap={5}
                    flexDirection={!showQuantities ? 'row' : 'column'}
                  >
                    <FlexItem>
                      <Text margin={0} color="gray9">
                        {orderItem.title}
                      </Text>
                    </FlexItem>
                    <FlexItem flexBasis={showQuantities ? '100%' : 'inherit'}>
                      <Text margin={0} color="gray5">
                        {OrderItemPrice.getPriceRepresentation()}
                      </Text>
                    </FlexItem>
                  </FlexItems>
                </FlexItem>
                {showQuantities && (
                  <FlexItem>
                    <OrderItemQuantitySelector
                      orderItem={orderItem}
                      onChange={(quantity) => {
                        // Update and call onChange.
                        onChange?.({
                          ...orderItem,
                          quantity: Number(quantity.code),
                        });
                      }}
                    />
                  </FlexItem>
                )}
              </FlexItems>
              <Spacing size="medium">
                <Hr />
              </Spacing>
            </div>
          )
        );
      })}
      <>
        {order.messages &&
          order.messages.map((message, index) => {
            return (
              <div key={index}>
                <div>
                  <Text tag="span" color="gray7">
                    {message}
                  </Text>
                </div>
                <Spacing />
              </div>
            );
          })}
      </>
      <Spacing size="large" />
      <div>
        <strong>
          <Text tag="span" color="gray8">
            <FormattedMessage
              id="order_subtotal_amount"
              defaultMessage="Subtotal"
            />
          </Text>
        </strong>{' '}
        : {OrderSubPrice.getPriceRepresentation()}
      </div>
      <Spacing />

      <>
        {order.adjustments &&
          order.adjustments.map((adjustment, index) => {
            const AdjustmentPrice = new Price(
              adjustment?.priceNumber,
              adjustment?.priceCurrency,
            );

            return (
              adjustment && (
                <div key={index}>
                  <div>
                    <strong>
                      <Text tag="span" color="gray8">
                        {adjustment.label}
                      </Text>
                    </strong>{' '}
                    : {AdjustmentPrice.getPriceRepresentation()}
                  </div>
                  <Spacing />
                </div>
              )
            );
          })}
      </>
      <Spacing size="large" />
      <FlexItems justifyContent="space-between">
        <FlexItem>
          <Text margin={0} color="gray9" fontWeight="bold">
            <FormattedMessage
              id="order_amount"
              defaultMessage="Total order amount"
            />
          </Text>
        </FlexItem>
        <FlexItem>
          <Text margin={0} color="gray5" fontWeight="bold">
            {OrderPrice.getPriceRepresentation()}
          </Text>
        </FlexItem>
      </FlexItems>
      <Spacing size="medium" />
      <CartOrderInfoSlot order={order} />
    </>
  );
};
